import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import { shouldComponentUpdate } from '../shallow_equal';
import ContentMenu from './content_menu.ui.jsx';
import { Link } from 'react-router';

const fromStore = PropTypes.any;
const linkTypeMap = {
    vimeo: 'Vimeo',
    wikipedia: 'Wikipedia',
    youtube: 'YouTube',
    '': 'External Link',
};

export default class ContentPreview extends React.Component {
    static displayName = 'ContentPreview';

    static makeLink(href, external, children) {
        return React.createElement(
            external ? 'a' : Link,
            external ? { href, target: '_blank' } : { to: href },
            children
        );
    }

    static propTypes = {
        refreshContent: PropTypes.func,
        deleteContent: PropTypes.func.isRequired,
        content: PropTypes.shape({
            id: PropTypes.number.isRequired,
            is_courseload_administered: PropTypes.bool,
            is_finalized: PropTypes.bool,
            media_type: PropTypes.string.isRequired,
            opted_out: PropTypes.bool.isRequired,
            title: PropTypes.string.isRequired,
            url: PropTypes.string,
        }).isRequired,
        contentId: PropTypes.number,
        coverUrl: fromStore,
        isInstructor: fromStore,
        linkType: fromStore,
        section: PropTypes.shape({
            id: PropTypes.number.isRequired,
            term_id: PropTypes.number.isRequired,
        }).isRequired,
        analyticsMode: PropTypes.oneOf(['term', 'section']).isRequired,
        hideSharingQA: PropTypes.bool,
        setOrigin: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
    };

    componentDidMount() {
        const doPoll = () => {
            const { contentId, content, refreshContent } = this.props;
            if (content) {
                if (content.is_finalized) {
                    clearInterval(this.poll);
                    delete this.poll;
                    return;
                }

                refreshContent(contentId);
            }
        };

        this.poll = setInterval(doPoll, 30 * 1000);
        doPoll();
    }

    shouldComponentUpdate(nextProps, nextState) {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    componentWillUnmount() {
        if (this.poll) {
            clearInterval(this.poll);
        }
    }

    onDelete = () => {
        const { content, deleteContent } = this.props;
        const msg = `Are you sure you want to delete ${content.title}?`;
        if (window.confirm(msg)) {
            deleteContent(content);
        }
    };

    getMediaIcon(content) {
        switch (content.media_type) {
            case 'link':
                return 'cl-icon cl-icon-external-link-square';
            case 'embedded_link':
                return 'cl-icon cl-icon-external-link-square';
            case 'lti_link':
                return 'cl-icon cl-icon-external-link-square';
            default:
                return 'cl-icon cl-icon-external-link-square';
        }
    }

    isBlocked() {
        const {
            content: { opted_out },
        } = this.props;
        return opted_out;
    }

    setTermViewOrigin = () => {
        this.props.setOrigin();
    };

    renderAnalyticsLink() {
        const { content, isInstructor, analyticsMode, section } = this.props;

        const isReaderContent =
            content.is_finalized && content.media_type === 'lti1p3_link';

        if (isReaderContent && isInstructor) {
            let baseUrl;
            if (analyticsMode === 'term') {
                baseUrl = `term/${section.term_id}`;
            } else {
                baseUrl = `section/${section.id}`;
            }

            const linkTo = `${baseUrl}/analytics/${content.id}`;

            return (
                <Link
                    to={linkTo}
                    aria-haspopup="true"
                    className="btn-secondary"
                >
                    <i className="cl-icon cl-icon-signal" aria-hidden="true" />
                    View analytics
                    <span className="aural"> for: {content.title}</span>
                </Link>
            );
        }
    }

    renderCover() {
        const { content, coverUrl, linkType } = this.props;
        const mediaIconClasses = classnames([
            this.getMediaIcon(content),
            'media-icon',
        ]);
        const materialClasses = classnames(['material-artifact', linkType]);
        const { opted_out } = content;

        if (!content.is_finalized) {
            return (
                <div className="material-artifact">
                    <div className="loading-container">
                        <div className="loading-block">
                            <div className="loading">
                                <div className="title">
                                    The file is uploaded and queued for
                                    processing. It will be available within 1
                                    hour.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (coverUrl && linkType !== '') {
            const linkTypeIconClasses = classnames([
                'cl-icon',
                `cl-icon-${linkType}-square`,
            ]);
            return (
                <div
                    className={materialClasses}
                    style={{ backgroundImage: `url("${coverUrl}")` }}
                >
                    {opted_out ? (
                        <div className="title-opt-out">
                            You opted not to receive this title.
                        </div>
                    ) : null}
                    <i className={mediaIconClasses} />
                    <div className="external-link-location">
                        <i className={linkTypeIconClasses} />{' '}
                        {linkTypeMap[linkType]}
                    </div>
                </div>
            );
        }

        if (coverUrl) {
            return (
                <div
                    className={materialClasses}
                    style={{ backgroundImage: `url("${coverUrl}")` }}
                >
                    <i className={mediaIconClasses} />
                </div>
            );
        }

        const linkIcon =
            linkType === 'wikipedia'
                ? 'cl-icon cl-icon-wikipedia'
                : 'cl-icon cl-icon-external-link-square';

        return (
            <div className={materialClasses}>
                <i className={mediaIconClasses} />
                <i className={linkIcon}>{linkTypeMap[linkType]}</i>
            </div>
        );
    }

    renderCoverLink(href, external) {
        const { title, is_finalized } = this.props.content;
        if (href && is_finalized && !this.isBlocked()) {
            return ContentPreview.makeLink(
                href,
                external,
                <div>
                    {this.renderCover()}
                    <span className="aural">Open {title}</span>
                </div>
            );
        }

        return this.renderCover();
    }

    renderTitle(href, external) {
        const { content } = this.props;

        if (href && !this.isBlocked()) {
            return (
                <div className="title">
                    {ContentPreview.makeLink(
                        href,
                        external,
                        <span>
                            <span className="aural">Open </span>
                            {content.title}
                        </span>
                    )}
                </div>
            );
        }

        return <div className="title">{content.title}</div>;
    }

    render() {
        const {
            content,
            contentId,
            isInstructor,
            analyticsMode,
            hideSharingQA,
            section,
            push,
        } = this.props;
        if (!content) return null;

        const isBlocked = this.isBlocked();
        const external = content.media_type === 'link';
        const hasProtocol = /^https?:\/\//;

        let href = content.url;
        if (!hasProtocol.test(href)) {
            href = 'http://' + href;
        }

        const destHref = external ? href : `/content-${contentId}`;

        return (
            <div
                className={classnames('material-wrapper', {
                    blocked: isBlocked,
                })}
            >
                <ContentMenu
                    content={content}
                    isInstructor={isInstructor}
                    onDelete={this.onDelete}
                    analyticsMode={analyticsMode}
                    section={section}
                    isBlocked={isBlocked}
                    hideSharingQA={hideSharingQA}
                    push={push}
                />
                {this.renderCoverLink(destHref, external)}
                {this.renderTitle(destHref, external)}
                {this.renderAnalyticsLink()}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/content_preview.ui.jsx